import { ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import {
    SidebarMenuHeader,
    type SidebarMenuHeaderConfig,
} from './SidebarMenuHeader';
import { SidebarMenuSkeleton } from './SidebarMenuSkeleton';

const NAV_BUTTON_BASE =
    'w-full justify-between [&>span]:flex-1 [&>span]:min-w-0 [&>span]:w-full [&>span]:justify-between [&>span>span]:flex [&>span>span]:justify-between [&>span>span]:w-full [&>span>span]:items-center rounded-lg px-2 py-1.5 h-9 text-left text-sm-medium';

export interface SidebarMenuItem {
    id: string;
    label: string;
    count?: number;
    disabled?: boolean;
    testId?: string;
    separatorAfter?: boolean;
}

export type { SidebarMenuHeaderConfig } from './SidebarMenuHeader';

interface SidebarMenuProps {
    items: SidebarMenuItem[];
    activeId: string | null;
    onItemClick: (id: string) => void;
    header?: SidebarMenuHeaderConfig;
    footer?: ReactNode;
    isLoading?: boolean;
    className?: string;
    ariaLabel?: string;
}

export function SidebarMenu({
    items,
    activeId,
    onItemClick,
    header,
    footer,
    isLoading = false,
    className,
    ariaLabel,
}: SidebarMenuProps) {
    return (
        <div className={cn('w-full flex flex-col gap-4', className)}>
            {header && <SidebarMenuHeader header={header} />}
            <nav
                className="flex flex-col w-full"
                aria-label={ariaLabel ?? header?.title}
            >
                {isLoading ? (
                    <SidebarMenuSkeleton />
                ) : (
                    <>
                        {items.map((item) => {
                            const isActive = activeId === item.id;
                            return (
                                <div key={item.id} className="flex flex-col">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        disabled={item.disabled}
                                        onClick={() => onItemClick(item.id)}
                                        data-testid={item.testId}
                                        className={cn(
                                            NAV_BUTTON_BASE,
                                            isActive
                                                ? 'bg-grey-600 text-white-100 hover:bg-grey-600 hover:text-white-100'
                                                : 'bg-transparent text-grey-200 hover:bg-grey-650 hover:text-grey-100',
                                        )}
                                    >
                                        <span className="truncate flex-1 min-w-0">
                                            {item.label}
                                        </span>
                                        {item.count !== undefined && (
                                            <span
                                                className={cn(
                                                    'shrink-0 text-xs',
                                                    isActive
                                                        ? 'text-white-100'
                                                        : 'text-grey-400',
                                                )}
                                            >
                                                {item.count}
                                            </span>
                                        )}
                                    </Button>
                                    {item.separatorAfter && (
                                        <div className="mt-4" aria-hidden />
                                    )}
                                </div>
                            );
                        })}
                        {footer}
                    </>
                )}
            </nav>
        </div>
    );
}
